import { reportError } from './report-error';
import tippy from "tippy.js";

import {handleHomePaginationLinkAction} from './home-scroll';
import moment from 'moment';
import {handleTitleChoiceInput} from "./titleChoiceInput";
import {hideProInfo} from "./main";
import {initDuplicatePosEvents} from "./duplicate-pos";

let activeTooltips = [];

const MANDATORY_SELECTOR = '.--mandatory, [data-class*="--mandatory"]';

/**
 * Champ marqué obligatoire (placeholder rose) dans le bloc qui porte l'infobulle, ou ce bloc lui-même.
 * Pour un input, la marque peut être sur son wrapper (nom, téléphone : cf. getTarget()).
 */
function hasMandatoryField(reference) {
    return reference.matches(MANDATORY_SELECTOR)
        || reference.querySelector(MANDATORY_SELECTOR) !== null
        || (reference.matches('input, select, textarea') && reference.closest(MANDATORY_SELECTOR) !== null);
}

function mandatoryMention() {
    return '* ' + translate('app.common.mandatory_field', {}, 'validators');
}

function escapeTooltipText(raw) {
    return raw
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const FIELD_ALERT_SELECTOR = '.invalid-icon:not(.--hidden)[data-title], .warning-icon:not(.--hidden)[data-title]';

/**
 * Alerte en cours sur un champ de saisie : posée sur le champ (data-alert) ou portée par l'icône
 * d'alerte affichée à côté (téléphone, email, nom suspect). Elle prime sur la description du champ.
 */
function fieldAlert(reference) {
    if (!reference.matches('input, select, textarea')) {
        return null;
    }
    const icon = reference.parentElement?.querySelector(FIELD_ALERT_SELECTOR);
    return reference.getAttribute('data-alert') || icon?.getAttribute('data-title') || null;
}

function textTooltipContent(reference) {
    const alert = fieldAlert(reference);
    if (alert) {
        return alert;
    }
    const title = reference.getAttribute('data-title') || '';
    if (!hasMandatoryField(reference)) {
        return title;
    }
    // Nœud DOM et non HTML : data-title peut contenir une saisie utilisateur.
    const content = document.createElement('div');
    if (title !== '') {
        content.append(title, document.createElement('br'));
    }
    content.append(mandatoryMention());
    return content;
}

function inlineHtmlTooltipContent(reference) {
    const alert = fieldAlert(reference);
    if (alert) {
        return escapeTooltipText(alert).replace(/\n/g, '<br>');
    }
    const html = escapeTooltipText(reference.getAttribute('data-title') || '').replace(/\n/g, '<br>');
    if (!hasMandatoryField(reference)) {
        return html;
    }
    return (html !== '' ? html + '<br>' : '') + escapeTooltipText(mandatoryMention());
}

/**
 * Sélecteur de mois (datepicker jQuery UI sans grille de jours, cf. .hide-calendar) : affiche « MM yy »
 * dans $input et écrit « yy-mm » dans le champ caché $alt, qui porte la valeur envoyée au serveur.
 * onChange est appelé à la fermeture, une fois le mois choisi.
 */
export function initMonthPicker($input, $alt, { maxDate = null, onChange = null } = {}) {
    if (typeof $.fn.datepicker !== 'function') {
        console.error('initMonthPicker : jQuery UI datepicker non chargé');
        return;
    }

    const toDate = function (value) {
        if (!value) {
            return null;
        }
        const parts = String(value).split('-');
        return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, 1);
    };
    const selectedDate = toDate($alt.val());

    $input.datepicker({
        changeMonth: true,
        changeYear: true,
        dateFormat: 'MM yy',
        altField: $alt,
        altFormat: 'yy-mm',
        maxDate: toDate(maxDate),
        defaultDate: selectedDate,
        beforeShow: function (input) {
            $(input).datepicker('widget').addClass('hide-calendar');
        },
        onClose: function (dateText, inst) {
            $(this).datepicker('setDate', new Date(inst.selectedYear, inst.selectedMonth, 1));
            $(this).datepicker('widget').removeClass('hide-calendar');
            if (onChange) {
                onChange($alt.val());
            }
        }
    });

    if (selectedDate) {
        $input.datepicker('setDate', selectedDate);
    }
}

/**
 * Datepickers jQuery UI avec listes mois / année (changeMonth, changeYear) : listes en select2 compact, mois en
 * toutes lettres. jQuery UI reconstruit l'en-tête à chaque changement de mois : on se branche donc sur son rendu
 * (_updateDatepicker), une seule fois pour toute l'application. Le format du champ n'est pas touché (la tarification
 * PDV relit « MMM yyyy » côté serveur) : seuls les libellés de la liste changent.
 */
export function enhanceDatepickerMonthYearSelects() {
    if (typeof $.datepicker === 'undefined' || typeof $.fn.select2 !== 'function') {
        console.error('enhanceDatepickerMonthYearSelects : jQuery UI datepicker ou select2 non chargé');
        return;
    }
    if ($.datepicker.scorimmoMonthYearSelects) {
        return;
    }
    $.datepicker.scorimmoMonthYearSelects = true;

    const render = $.datepicker._updateDatepicker;
    $.datepicker._updateDatepicker = function (inst) {
        render.apply(this, arguments);

        const $selects = inst.dpDiv.find('select.ui-datepicker-month, select.ui-datepicker-year');
        if ($selects.length === 0) {
            return;
        }
        const monthNames = this._get(inst, 'monthNames');
        inst.dpDiv.find('select.ui-datepicker-month option').each(function () {
            $(this).text(monthNames[this.value] ?? $(this).text());
        });
        // Liste ouverte dans le calendrier : un clic hors de #ui-datepicker-div le fermerait.
        $selects.select2({ dropdownParent: inst.dpDiv, minimumResultsForSearch: Infinity });

        // Largeur du mois figée sur le nom le plus large (mesuré, la police n'étant pas à chasse fixe) : elle ne
        // varie plus d'un mois à l'autre.
        const $monthContainer = inst.dpDiv.find('select.ui-datepicker-month').next('.select2-container');
        const $rendered = $monthContainer.find('.select2-selection__rendered');
        if ($rendered.length) {
            const currentText = $rendered.text();
            const widest = Math.max(...monthNames.map((name) => {
                $rendered.text(name);
                return $monthContainer.outerWidth();
            }));
            $rendered.text(currentText);
            $monthContainer.css('min-width', Math.ceil(widest) + 'px');
        }
    };
}

/** Recalcule une infobulle déjà affichée après changement de son data-title (ex. suivi de la souris). */
export function refreshTooltip(el) {
    el._tippy?.setContent(el.classList.contains('--tooltip-inline-html') ? inlineHtmlTooltipContent(el) : textTooltipContent(el));
}

const TOOLTIP_SELECTOR = '.--tooltip-html, .--tooltip-txt, .--tooltip-inline-html';

// Infobulle imbriquée (picto « ! » dans un champ qui a la sienne) : une seule à la fois, celle de l'élément
// survolé le plus proche. false annule l'affichage (convention tippy onShow).
function showOnlyInnermostTooltip(tooltip) {
    if (tooltip.reference.querySelector(`:is(${TOOLTIP_SELECTOR}):hover`)) {
        return false;
    }
    for (let parent = tooltip.reference.parentElement?.closest(TOOLTIP_SELECTOR); parent; parent = parent.parentElement?.closest(TOOLTIP_SELECTOR)) {
        parent._tippy?.hide();
    }

    return true;
}

export function launchTooltip() {
    // Détruire tous les anciens tooltips
    activeTooltips.forEach(instance => instance.destroy());
    activeTooltips = [];
    const getPlacement = (el) => el.getAttribute('data-placement') || 'top';
    // data-trigger="mouseenter" sur un champ de saisie : pas d'infobulle au focus, qui masquerait la frappe
    const getTrigger = (el) => el.getAttribute('data-trigger') || 'mouseenter focus';

    // Tooltips HTML
    document.querySelectorAll('.--tooltip-html').forEach(el => {
        const instance = tippy(el, {
            content(reference) {
                const id = reference.getAttribute('data-template');
                const template = document.getElementById(id);
                return template?.innerHTML || '';
            },
            allowHTML: true,
            placement: getPlacement(el),
            onShow: showOnlyInnermostTooltip,
        });

        if (Array.isArray(instance)) {
            activeTooltips.push(...instance);
        } else {
            activeTooltips.push(instance);
        }
    });

    // Tooltips texte. Contenu recalculé à chaque affichage : un champ peut devenir obligatoire
    // après l'initialisation (ex. adresse de facturation des tarifs PDV).
    document.querySelectorAll('.--tooltip-txt').forEach(el => {
        const instance = tippy(el, {
            content: textTooltipContent,
            onShow(tooltip) {
                if (!showOnlyInnermostTooltip(tooltip)) {
                    return false;
                }
                tooltip.setContent(textTooltipContent(tooltip.reference));
            },
            placement: getPlacement(el),
            trigger: getTrigger(el),
        });

        if (Array.isArray(instance)) {
            activeTooltips.push(...instance);
        } else {
            activeTooltips.push(instance);
        }
    });

    // Tooltips inline HTML (uniquement saut de ligne : le texte est échappé avant
    // conversion des \n en <br>, pour empêcher l'injection de balises via data-title
    // qui contient du texte potentiellement saisi par un utilisateur)
    document.querySelectorAll('.--tooltip-inline-html').forEach(el => {
        const instance = tippy(el, {
            content: inlineHtmlTooltipContent,
            onShow(tooltip) {
                if (!showOnlyInnermostTooltip(tooltip)) {
                    return false;
                }
                tooltip.setContent(inlineHtmlTooltipContent(tooltip.reference));
            },
            allowHTML: true,
            placement: getPlacement(el),
        });

        if (Array.isArray(instance)) {
            activeTooltips.push(...instance);
        } else {
            activeTooltips.push(instance);
        }
    });
}

function handleClosePopinAction()
{
    $('.custom-popin .popin-close').unbind('click').on('click', function(event) {
        event.preventDefault();
        closePopin();
    });
}

/**
 * Branche la validation asynchrone d'un champ email sur la route `user_email_validation`.
 * Met à jour le tooltip (data-title + classe `--warning --tooltip-txt`) et notifie l'appelant.
 *
 * @param {jQuery} $emailField champ email
 * @param {{ baseTitle: string, onResult?: (isValid: boolean) => void }} options
 *   baseTitle : titre restauré quand le champ est vide/valide.
 *   onResult  : callback optionnel (valide sur transition true/false, y compris champ vide).
 */
export function bindEmailValidation($emailField, { baseTitle, onResult }) {
    if ($emailField.length === 0) return;
    const $fieldWrapper = $emailField.closest('.field');

    function paint(warning, title) {
        $fieldWrapper.attr('data-title', title).toggleClass('--warning', warning);
        if (warning) {
            $fieldWrapper.addClass('--tooltip-txt');
        }
        launchTooltip();
    }

    function validate() {
        const value = ($emailField.val() || '').trim();
        if (!value) {
            paint(false, baseTitle);
            onResult?.(false);
            return;
        }
        $.post(Routing.generate('user_email_validation'), { email: value }, function (data) {
            const isValid = !!(data && data.isValid);
            if (isValid && data.normalizedEmail) {
                $emailField.val(data.normalizedEmail);
            }
            paint(!isValid, isValid ? baseTitle : (data?.message || baseTitle));
            onResult?.(isValid);
        });
    }

    $emailField.on('change', validate);
    $(document).ready(validate);
}

/**
 * Construit un objet {champ: jQuery(#prefix+champ)} à partir d'une liste de noms de champs —
 * mutualisé entre form-user.js et form-call-user.js, qui ne diffèrent que par la liste de
 * champs et le préfixe d'id (`seller_` / `call_user_`), pas par la mécanique de lookup.
 */
export function buildRightInputs(container, fields, prefix) {
    const inputs = {};
    fields.forEach(field => { inputs[field] = container.find('#' + prefix + field); });
    return inputs;
}

export function setupParentChildRightsToggle(parentInput, childInputs, defaultsOnEnable, showRight, hideRight) {
    if (parentInput.is(':checked')) {
        childInputs.forEach(showRight);
    } else {
        childInputs.forEach(input => input.parents('.field').addClass('--hidden'));
    }

    parentInput.change(function () {
        if (parentInput.is(':checked')) {
            childInputs.forEach(showRight);
            defaultsOnEnable.forEach(input => input.prop('checked', true).trigger('change'));
        } else {
            childInputs.forEach(hideRight);
        }
    });
}

export function closePopin() {
    $('.custom-popin').remove();
    $('.opacity-layer').remove();
}

/**
 * Affiche un bandeau flash-message (markup .flash-message/.flash-success/.flash-error/
 * .close-flash, cf. templates/webhook/_test_flash.html.twig et équivalents) au-dessus d'une
 * section, avec bascule de classe et d'icône, et auto-masquage après 15s en cas de succès
 * (une erreur reste affichée jusqu'à dismissal explicite ou prochain appel).
 * `message` est inséré en texte brut (il peut citer un email, un PDV ou une réponse serveur) ;
 * un tableau est affiché une entrée par ligne.
 */
export function showFlash(flash, message, hasError) {
    setTextLines(flash.find('.text'), Array.isArray(message) ? message : [message]);
    flash.toggleClass('flash-success', !hasError).toggleClass('flash-error', hasError);
    flash.find('.scorimmo-icon-Other-Checkmark-2, .scorimmo-icon-Other-No-Entry')
        .attr('class', hasError ? 'scorimmo-icon-Other-No-Entry' : 'scorimmo-icon-Other-Checkmark-2');
    flash.removeClass('--hidden');

    if (!hasError) {
        setTimeout(function () {
            flash.addClass('--hidden');
        }, 15000);
    }
}

/**
 * Remplace le contenu de `$target` par les lignes données, en texte brut séparé par des <br>.
 */
export function setTextLines($target, lines) {
    $target.empty();
    lines.forEach(function (line, index) {
        if (index > 0) {
            $target.append(document.createElement('br'));
        }
        $target.append(document.createTextNode(line == null ? '' : String(line)));
    });
}

export function popLink() {
    // Handler mutualisé : force le pop-out et bloque les raccourcis navigateur
    // (clic molette, ctrl/cmd+clic) qui contournaient l'ouverture en fenêtre
    // dédiée en ouvrant un nouvel onglet. Comportement identique aux liens
    // d'ouverture d'email.
    const handler = function(event, url) {
        // Ignore clic droit
        if (event.button === 2) return;

        // Alt : ouverture explicite dans un nouvel onglet
        if (event.altKey) {
            event.preventDefault();
            window.open(url, '_blank');
            return;
        }

        // Chrome/Safari macOS déclenchent l'ouverture en nouvel onglet dès le
        // mousedown pour cmd/ctrl+clic gauche : on doit donc intercepter à ce
        // stade, sinon un preventDefault sur `click` arrive trop tard. Sur le
        // clic gauche simple on garde le déclenchement sur `click` classique.
        const isLeftMouseDown  = event.type === 'mousedown' && event.button === 0;
        const isLeftClick      = event.type === 'click'     && event.button === 0;
        const isMiddleMouseDown = event.type === 'mousedown' && event.button === 1;
        const withModifier     = event.ctrlKey || event.metaKey;

        if (isLeftMouseDown && withModifier) {
            event.preventDefault();
            window.open(url, Date.now(), "width=1680,height=950,popup=true");
            $(event.currentTarget).data('popLinkSkipClick', true);
            return;
        }

        if (isLeftClick) {
            event.preventDefault();
            if ($(event.currentTarget).data('popLinkSkipClick')) {
                $(event.currentTarget).removeData('popLinkSkipClick');
                return;
            }
            window.open(url, Date.now(), "width=1680,height=950,popup=true");
            return;
        }

        if (isMiddleMouseDown) {
            event.preventDefault();
            window.open(url, Date.now(), "width=1680,height=950,popup=true");
        }
    };

    // Cas href classique
    $('.pop-link[href]').off('click.popLink mousedown.popLink').on('click.popLink mousedown.popLink', function(event) {
        const elementClassName = event.target.className;
        if (typeof elementClassName === 'string' && elementClassName.includes('--no-pop')) {
            return;
        }
        const url = $(this).attr('href');
        if (!url || url === '#') return;
        handler(event, url);
    });

    // Cas data-href
    $('.pop-link[data-href]').off('click.popLink mousedown.popLink').on('click.popLink mousedown.popLink', function(event) {
        const url = $(this).data('href');
        if (!url) return;
        handler(event, url);
    });
}


export function submitLinks() {
    $('.popin-content a.submitLink').click(function (event) {
        let formComment = $('#formComment');
        if ($(this).text() !== 'Autre') {
            event.preventDefault();
            let url = $(this).attr('href');
            formComment.attr('action', url);
            formComment.submit();
        } else {
            if ($('#note').val() === '') {
                $('#emptyComment').removeClass('--hidden');
                return false;
            } else {
                event.preventDefault();
                let url = $(this).attr('href');
                formComment.attr('action', url);
                formComment.submit();
            }
        }

    });
}

export function customForm() {
    if ($("#point_of_sale_parentPos").length > 0) {
        $("#point_of_sale_parentPos").select2({
            templateResult: formatPosOption,
            templateSelection: formatPosOption,
        });
    }
    customDatePicker();
}

export function customDatePicker() {
    if (typeof $.fn.datepicker !== 'function') {
        return;
    }
    $('input[type="date"].datepicker').prop('type', 'text');
    let $datepicker = $('.datepicker');
    $datepicker.datepicker({
        beforeShow: function (input, inst) {
            setTimeout(function() {
                const $datepicker = inst.dpDiv;
                const $input = $(input);
                $datepicker.css({
                    top: $input.offset().top + $input.offsetHeight,
                    left: $input.offset().left
                });
            }, 10);
        }
    });
    $datepicker.datepicker('option', 'dateFormat', 'dd/mm/yy');
    $('.datepicker.stroke').datepicker('option', 'dateFormat', 'dd-mm-yy');
    $('.datepicker.minToday').datepicker("option", "minDate", new Date());
    $('.datepicker.max3months').datepicker("option", "maxDate", '+3M').on('change', function() {
        const container = $(this).parents('.datetime');
        container.find('ul.errors').remove();
        const arr = $(this).val().split('/');
        const date = new Date(arr[2],arr[1]-1,arr[0]);
        if ( Object.prototype.toString.call(date) === "[object Date]" ) {
            if ($(this).hasClass('no-message')) {
                return;
            }
            if ( !isNaN(date.getTime()) ) {
                let max = new Date ();
                max.setMonth(max.getMonth() + 3);
                if (date.getTime() > max.getTime()) {
                    container.prepend('<ul class="errors"><li>'+translate('app.date.max_months', {'months': 3})+'</li></ul>');
                } else {
                    container.find('ul.errors').remove();
                }
            } else {
                container.prepend('<ul class="errors"><li>'+translate('app.date.invalid')+'</li></ul>');
            }
        } else {
            container.prepend('<ul class="errors"><li>'+translate('app.date.invalid')+'</li></ul>');
        }
    });

    $('.datepicker.max-custom').each(function() {
        const maxDateAttr = $(this).attr('data-max');
        if (maxDateAttr) {
            const maxParts = maxDateAttr.split('-');
            const maxDate = new Date(maxParts[0], maxParts[1] - 1, maxParts[2]); // Format YYYY-MM-DD
            $(this).datepicker('option', 'maxDate', maxDate);
        }
    })
}

/**
 * Option d'un select univers : pastille de la couleur de l'univers (.--interest-N, cf.
 * templates/common/_interests.css.twig) devant le libellé. Sert aussi de templateSelection
 * sur un select univers simple (pas de puce).
 */
export function formatInterestOption(data, interestId) {
    if (!data.id) return data.text;
    const $row = $('<span class="select2-option-wrapper"></span>');
    if (interestId) {
        $row.append($('<span class="select2-color-dot"></span>').addClass('--interest-' + interestId));
    }
    return $row.append($('<span>').text(data.text));
}

// Rendu coloré partagé des puces "univers" en sélection multiple (select2 insère son DOM après
// l'appel de templateSelection, d'où le setTimeout pour poser la classe une fois le badge en place).
export function formatInterestSelection(data, interestId) {
    if (!data.id) return data.text;
    const $result = $('<span>').text(data.text);
    setTimeout(function () {
        $result.closest('.select2-selection__choice').addClass('interest-' + interestId + ' --colored');
    });
    return $result;
}

/**
 * Option d'un select PDV marqué data-mark-overdue : nom en gras, type (Groupe/Plaque) en gris,
 * mention « Impayé » en italique rouge, ligne grisée si data-inactive="1". Les libellés viennent
 * des data-* posés côté serveur (déjà traduits, cf. PointOfSaleService::selectOptionAttributes()).
 */
export function formatPosOption(data) {
    if (!data.id || !data.element) {
        return data.text;
    }
    const { name, type, overdueLabel, inactive } = data.element.dataset;
    const $row = $('<span></span>');
    if (inactive === '1') {
        $row.addClass('--grey');
    }
    $row.append($('<strong></strong>').text(name || data.text));
    if (type) {
        $row.append($('<span class="pos-type"></span>').text(' (' + type + ')'));
    }
    if (overdueLabel) {
        $row.append($('<em class="pos-overdue"></em>').text(overdueLabel));
    }
    return $row;
}

/**
 * Option d'un filtre statut à pictogramme (data-icon posé par common/_status_select_options.html.twig) :
 * pictogramme + libellé dans la liste, pictogramme seul une fois sélectionné (colonnes statut étroites ;
 * libellé en infobulle via handleIconSelectTooltip).
 */
export function formatStatusIconOption(data, iconOnly) {
    const icon = data.element ? data.element.dataset.icon : null;
    if (!data.id || !icon) {
        return data.text;
    }
    const $icon = $('<span class="status-option-icon"></span>').addClass(icon);
    if (iconOnly) {
        return $icon;
    }
    return $('<span class="status-option"></span>').append($icon, $('<span></span>').text(data.text));
}

export function formatOption(data) {
    if (!data.id) return data.text;

    // 1: avant les : ou (   — non-greedy pour laisser la parenthèse capturer part3
    // 2: après les :         (optionnel)
    // 3: contenu entre parenthèses (optionnel)
    // Sans exclure `(` du group 1, la version gloutonne avalait la parenthèse dans part1
    // et le rôle n'était jamais séparé (tout apparaissait en gras noir sur le seller select).
    let regex = /^([^:(]+?)(?:\s*:\s*([^()]+?))?(?:\s*\(([^)]+)\))?\s*$/;
    let match = data.text.match(regex);

    if (!match) return data.text;

    let part1 = match[1].trim();
    let part2 = match[2] ? match[2].trim() : null;
    let part3 = match[3] ? match[3].trim() : null;

    const $wrapper = $('<span class="select2-option-wrapper"></span>')
        .append($('<span class="select2-part1"></span>').text(part1));

    if (part2) {
        $wrapper.append(
            ' ',
            $('<span class="select2-part1-separator"></span>').text('\u00a0: '),
            $('<span class="select2-part2"></span>').text(part2)
        );
    }

    if (part3) {
        $wrapper.append(' ', $('<span class="select2-part3"></span>').text('(' + part3 + ')'));
    }

    return $wrapper;
}

export function handlePopinAction()
{
    customDatePicker();
    handleTitleChoiceInput();
    handleClosePopinAction();
    hideProInfo();
    if ($('.popin-content select:not(#sda_viadService, #viad_service_incomingSda, .viad-sip-select, .select-origin, .select-email-origin, .webhook-interests-select)').length > 0) {
        $('.popin-content select:not(#sda_viadService, #viad_service_incomingSda, .viad-sip-select, .select-origin, .select-email-origin, .webhook-interests-select)').each(function() {
            $(this).select2({
                placeholder: $(this).data('placeholder'),
                allowClear: $(this).data('allow-clear') === 1,
                ...($(this).is('[data-mark-overdue]') ? {
                    templateResult: formatPosOption,
                    templateSelection: formatPosOption,
                } : {}),
            });
        });
    }

    // Origine des traceurs SDA/email dans les popins : liste par défaut + saisie libre
    $('.popin-content .select-origin, .popin-content .select-email-origin').each(function() {
        initAjaxSelect($(this), Routing.generate('default_origins'), true, false);
    });

    if ($('.viad-sip-select').length > 0) {
        $('.viad-sip-select').each(function() {
            $(this).select2({
                placeholder: $(this).data('placeholder'),
                allowClear: $(this).data('allow-clear') === 1,
                templateResult: formatOption,
                templateSelection: formatOption,
            });
        });
    }

    // Univers du webhook : même rendu coloré (via templates/common/_interests.css.twig) que le
    // multiselect univers de la fiche PDV (cf. initInterestSelects() dans admin.js).
    if ($('.webhook-interests-select').length > 0) {
        $('.webhook-interests-select').each(function() {
            const $select = $(this);
            $select.select2({
                placeholder: $select.data('placeholder'),
                allowClear: true,
                templateResult: data => formatInterestOption(data, data.id),
                templateSelection: function(data) {
                    return formatInterestSelection(data, data.id);
                },
            });
        });
    }

    if ($('#viad_service_incomingSda').length > 0) {
        $('#viad_service_incomingSda').select2({
            placeholder: $(this).data('placeholder'),
            templateResult: formatOption,
            templateSelection: formatOption,
        });
    }
    const viadServiceSelect = $('#popinFormContainer select#sda_viadService');
    if (viadServiceSelect.length > 0) {
        viadServiceSelect.select2({
            placeholder: $(this).data('placeholder'),
            templateResult: formatOption,
            templateSelection: formatOption,
        });

        const sdaPrefixesSelect = $('#sda_prefix');
        const allOptionsClone = sdaPrefixesSelect.find('option').clone(); // Garde toutes les options originales

        function filterSdaPrefixes() {
            const selectedOption = viadServiceSelect.find('option:selected');
            const allowedPrefixes = selectedOption.data('prefixes'); // ex: "01,02,03"
            const allowedArray = allowedPrefixes ? allowedPrefixes.split(',').map(p => p.trim()) : [];

            const currentValue = sdaPrefixesSelect.val();
            sdaPrefixesSelect.empty();

            allOptionsClone.each(function () {
                const value = $(this).val();
                if (value === "" || allowedArray.length === 0 || allowedArray.includes(value)) {
                    sdaPrefixesSelect.append($(this).clone());
                }
            });

            if (currentValue && allowedArray.includes(currentValue)) {
                sdaPrefixesSelect.val(currentValue);
            } else {
                sdaPrefixesSelect.val('');
            }

            sdaPrefixesSelect.select2('destroy').select2();
        }

        // Filtrer au changement
        viadServiceSelect.on('change', filterSdaPrefixes);

        // Filtrer immédiatement à l'initialisation
        filterSdaPrefixes();
    }

    popinFunctions();

    $(document).off('click', '.popin-button').on('click', '.popin-button', function(e) {
        $('.custom-popin').remove();
        $('.opacity-layer').remove();
        e.preventDefault();
        let url = $(this).attr('href');
        $.ajax(url)
            .done(function(response){
                $('body').append('<div class="opacity-layer"></div>');
                let html = response;
                if (response.html) {
                    html = response.html;
                }
                $('body').append('<div class="custom-popin">'+html+'</div>');
                //triggerForms(); (pour enregistrer reaffect en ajax ?)
                handlePopinAction();
                launchTooltip();
                submitLinks(); // pour enregistrer la valeur du champ "commentaire" (par exemple sur la popin "classer sans suite")
                handleChangePos();
                handlePopinLeadForm($('#reaffect'));
                handlePopinLeadForm($('#lostDismiss'));
                handlePopinLeadForm($('#lostValidate'));
                initDuplicatePosEvents();
                if (response.url) {
                    setTimeout(function () {
                        window.location = response.url;
                    }, 2000);
                }
                handleClosePopinAction();
            });
    });
}

export function loadMultipleSelect(select) {
    if (select.length === 0) {
        return;
    }
    let selected = select.val();
    if (selected !== null) {
        selected = selected.toString();
        $('#select2seller').val(selected.split(',')).trigger('change');
    }
}


export function loadSellersList(select) {

    let posId = $('#select2pos').val();
    let sellerSel = $('#select2seller');
    if (sellerSel.length === 0) {
        return;
    }
    let url = $(select).data('url');
    let selected = sellerSel.data('selected');
    if (selected !== undefined) {

        selected = selected.toString().split(',');
    }

    if (posId === undefined || posId === null || posId.length === 0)
        return;
    url = url + '/' + posId;
    // Forcé par les vues seller+ (filtre "équipe" absences/validation-leads-sans-suite/stats) :
    // exclure les virtuels côté route AJAX, quel que soit le rôle du user connecté.
    if (sellerSel.data('exclude-virtual')) {
        url += '?excludeVirtual=1';
    }

    $.get(url, function (data) {
        sellerSel.empty();
        // Liste ordonnée par le serveur (tri Prénom Nom) — un `$.each` sur un dict
        // aurait ré-ordonné les clés numériques et cassé le tri.
        data.forEach(function (item) {
            let elt = $('<option>').attr('value', item.id).text(item.label);
            if (selected != null && selected.indexOf(String(item.id)) !== -1) {
                elt.attr('selected', 'selected');
            }
            sellerSel.append(elt);
        });

        loadMultipleSelect(sellerSel);
    });
}

export function redirectWithParams(redirect, element, nobloc) {

    redirect = typeof redirect !== 'undefined' ? redirect : true;
    nobloc = typeof nobloc !== 'undefined' ? nobloc : false;

    let data = [];
    const elementsToProcess = [
        { id: '#select2seller', variable: 'seller' },
        { id: '#select2pos', variable: 'pos' },
        { id: '#selectOperator', variable: 'operator' },
        { id: '#channel', variable: 'channel' },
        { id: '#interest', variable: 'interest' },
        { id: '#search_keywords', variable: 'search_keywords' },
        { id: '#channel', variable: 'channel' },
        { id: '#originFilter', variable: 'origin' },
        { id: '#evolution-range', variable: 'evolution-range' },
        { id: '#references', variable: 'references' },
        { id: '#libelles', variable: 'libelle' },
    ];

    for (let i = 0; i < elementsToProcess.length; i++) {
        let elementInfo = elementsToProcess[i];
        let select = $(elementInfo.id);
        let elementValue = select.val();
        let value = '';

        if (select.prop('multiple')) {
            if (elementValue != null && $.isArray(elementValue)) {
                value = elementValue;
                if (value.length === 0) {
                    value = 0;
                }
            }
        } else {
            value = elementValue;
        }
        if (typeof value != 'undefined') {
            data[elementInfo.variable] = value === null ? '' : value;
        }
    }

    let start = null;
    let end =  null;
    let range = null;
    let filterRange = $("#filter-range");

    let rangePicker = filterRange.daterangepicker('getRange');
    if (rangePicker != null) {
        start = moment(filterRange.daterangepicker('getRange').start).format('DD-MM-YYYY');
        end = moment(filterRange.daterangepicker('getRange').end).format('DD-MM-YYYY');
        range = 'custom';
    }
    let bloc;
    let sortOrder;
    let sortColumn;

    if (nobloc === false) {
        bloc = $('#idBlock').val();
    }

    if (typeof element != 'undefined') {
        sortOrder = element.attr('data-sort-order');
        sortColumn = element.attr('data-sort-column');
    }
    let route = $('#contentFilter .route').val();

    if (nobloc === false && typeof bloc != 'undefined' && bloc !== '') {
        data['bloc'] = bloc;
    }
    if (typeof sortOrder !== 'undefined' && sortOrder !== '') {
        data['orderby'] = sortOrder;
    }
    if (typeof sortColumn !== 'undefined' && sortColumn !== '') {
        data['sort'] = sortColumn;
    }
    if (range != null) {
        data['dbrange'] = range;
    }
    if (start != null && typeof start !== 'undefined' && start !== '') {
        data['start'] = start;
    }
    if (end != null && typeof end !== 'undefined' && end !== '') {
        data['end'] = end;
    }
    if (redirect) {
        window.location = Routing.generate(route, data);
    }
    else {
        return Routing.generate(route, data);
    }
}


function updateSellersByPos(pos, sellers, except) {
    let interest = $('#leadInterest').val();
    $.get(Routing.generate('get_sellers_by_pos', {
        'pos': pos,
        'interest': interest,
        'except': except
    }), function (data) {
        sellers.empty();
        let option = '';
        $.each(data, function(key, value) {
            option = $("<option></option>").val(key).text(value);
            sellers.append(option);
        });
        sellers.trigger('destroy');
        sellers.select2();
    });
}

export function handleChangePos() {
    let lostDismissLeadOwnerGroup = $('#lost_dismiss_lead_ownerGroup');
    if (lostDismissLeadOwnerGroup.length > 0) {
        updateSellersByPos(lostDismissLeadOwnerGroup.val(), $("#lost_dismiss_lead_seller"));
        lostDismissLeadOwnerGroup.on('change', function() {
            updateSellersByPos($(this).val(), $("#lost_dismiss_lead_seller"));
        })
    }
    let leadOwnerGroup = $('#lead_ownerGroup');
    if ($('.can-change #lead_ownerGroup').length > 0) {
        let except = $('#assignment_lead_seller').val();
        updateSellersByPos(leadOwnerGroup.val(), $('#lead_seller'), except);
        leadOwnerGroup.on('change', function() {
            updateSellersByPos($(this).val(), $('#lead_seller'), except);
        })
    }
    if ($('.can-change #assignment_lead_ownerGroup').length > 0) {
        let except = $('#assignment_lead_currentSeller').val();
        let assignmentLeadOwnerGroup = $('#assignment_lead_ownerGroup');
        updateSellersByPos(assignmentLeadOwnerGroup.val(), $('#assignment_lead_seller'), except);
        assignmentLeadOwnerGroup.on('change', function() {
            updateSellersByPos($(this).val(), $('#assignment_lead_seller'), except);
        })
    }
}

function validateEditHourAction() {

    let validateEditHourButton = $('#editHour').find('.--confirm');
    validateEditHourButton.click(() => {
        let form = $('#editHour');
        let url = form.attr('action');
        let formSerialize = form.serialize();

        $.post(url, formSerialize, function (response) {
            if (response === 'success') {
                location.reload();
            } else {
                $('#hour').replaceWith(response);
                validateEditHourAction();
            }
        })
            .fail(function (jqXHR) {
                reportError('popin form XHR failed', jqXHR, { status: jqXHR.status });
            })
        ;
    });
}

function validateFormAction() {

    let popinFormButton = $('#popinForm').find('.--confirm');
    popinFormButton.click(() => {

        const popinFormContainer = $('#popinFormContainer');
        const table = $('.' + popinFormContainer.attr('data-table'));
        const posId = popinFormContainer.attr('data-pos-id');
        const loader = popinFormContainer.find('.common-loader');
        const content = popinFormContainer.find('.content');
        loader.removeClass('--hidden');
        content.addClass('--hidden');

        let form = $('#popinForm');
        let url = form.attr('action');
        let formSerialize = form.serialize();

        $.post(url, formSerialize, function (response) {
            if (response.success) {
                if (response.route) {
                    updateTableContent(table, response.route, posId);
                } else {
                    location.reload();
                }
            } else {
                $('#popinFormContainer').replaceWith(response.html);
                handlePopinAction();
                launchTooltip();
                getTelecomService();
                validateFormAction();
                loader.addClass('--hidden');
                content.removeClass('--hidden');

            }
        })
            .fail(function (jqXHR) {
                reportError('popin form XHR failed', jqXHR, { status: jqXHR.status });
            })
        ;
    });
}

export function updateTableContent(table, route, posId) {


    const popConfirmation = $('body > .pop-confirmation, body > .custom-popin');
    const popOverlay = $('body > .pop-overlay, body > .opacity-layer');
    $('.common-loader').removeClass('--hidden');

    const url = Routing.generate(route, {'id': posId});

    $.get(url, function (response) {
        const newTable = $(response.html.trim());
        table.replaceWith(newTable);
        refreshTracerUsage(newTable);
        launchTooltip();
    }).fail(function (jqXHR) {
        reportError('table refresh XHR failed', jqXHR, { status: jqXHR.status });
        // L'ajout ou la suppression a abouti côté serveur : recharger affiche l'état réel.
        location.reload();
    }).always(function () {
        popOverlay.addClass('--hidden');
        popConfirmation.addClass('--hidden');
        $('.common-loader').addClass('--hidden');
    });
}

/**
 * Le badge d'utilisation (page numéros d'un PDV) est dans l'en-tête de section, hors de la table
 * rechargée : il est resynchronisé depuis les data-usage-* de la nouvelle table.
 */
function refreshTracerUsage(newTable) {
    const badge = newTable.closest('section').find('.js-tracer-usage');
    if (!badge.length) {
        // Les autres tables rechargées par updateTableContent() n'ont pas de badge.
        return;
    }
    const label = newTable.attr('data-usage-label') || '';
    const color = newTable.attr('data-usage-color') || 'red';
    badge.text(label)
        .attr('data-title', newTable.attr('data-usage-title') || '')
        .toggleClass('--grey', color === 'grey')
        .toggleClass('--red', color !== 'grey')
        .toggleClass('--hidden', label === '');
}


function updateServiceData() {
    const selectedOption = $('#viad_service_key').find('option:selected');
    const serviceName = selectedOption.data('service-name');
    $('#viad_service_label').val(serviceName);
    const serviceDescription = selectedOption.data('service-description');
    $('#serviceDescription').text(serviceDescription);
    const recordingAllowed = selectedOption.data('recording-allowed');
    if (recordingAllowed) {
        $('.recording').removeClass('--hidden');
        if (selectedOption.parents('#popinFormContainer').hasClass('--add')) {
            // mise à jour du champ d'enregistrement uniquement en cas de création
            $('#viad_service_randomRecording').prop('checked', true);
        }
    } else {
        $('#viad_service_randomRecording').prop('checked', false);
        $('.recording').addClass('--hidden');
    }
    const hasSdaField = selectedOption.data('has-sda-field');
    if (hasSdaField) {
        $('.--sdas').show();
    } else {
        $('.--sdas').hide();
    }
    if (selectedOption.parents('#popinFormContainer').hasClass('--update')) {
        $('#viad_service_key').next('.select2-container').addClass('readonly');
    }

}
function getTelecomService()
{
    if ($('#viad_service_key').length > 0) {
        updateServiceData();
        $('#viad_service_key').on('change', updateServiceData);
    }
}

function popinFunctions() {
    validateEditHourAction();
    getTelecomService();
    validateFormAction();
    preventDoublon();
}

export function updateSellers()
{
    let pos = $('#lead_pos').val();
    let leadTeam = $( "#lead_team" );
    if (leadTeam.length <= 0) {
        return;
    }
    let interest = $('#lead_interest input:checked').val();
    if (typeof interest !== 'undefined') {

        //show loader
        leadTeam.find('.common-loader').removeClass('--hidden');
        leadTeam.find('.section-content .sellers').html('')

        let url = Routing.generate('lead_sellers_list', {'id': pos, 'interestId': interest});

        $.get( url, function( data ) {
            leadTeam.replaceWith( data );
            $('#selectPos').select2();
            sortingSellersHandler();
            // Seller list auto-selection if user fill reference or zipcode field
            autoSelectSeller();
            bindSellerTagHighlight();
            if ($('#value_lead_id').val() !== "") {
                preSelectSellerLine();
            }
            let sellerLine = $('.seller-line');
            if (sellerLine.length === 1) {
                toggleSellerLineSelection(sellerLine);
            }
            // si pas de vendeur sélectionné on déselectionne dans le select
            if ($('.seller-line.seller-selected').length === 0) {
                $('#lead_seller').find('option:selected').prop('selected', false);
            }
            if (isLeadLocked()) {
                // Après la présélection, qui coche la ligne du vendeur actuel : une case désactivée ne se coche plus
                $('#lead_team').addClass('--locked').find('input, select').prop('disabled', true);
            }
            applyTransferLock();
            launchTooltip();
            bindCalendlyPrefillLinks();
            //hide loader
            leadTeam.find('.common-loader').addClass('--hidden');
            loadSellerHints();
        });
    } else {
        // remove sellers
        leadTeam.find('.section-content').html('<div class="interest-empty empty-data empty-state">' + translate('app.lead.form.no_interest') + '</div>');
    }
}

/**
 * Charge et affiche les indices d'affectation dans la section équipe :
 * - Dernière affectation par référence de bien
 * - Prospect déjà en contact avec un vendeur
 *
 * Cliquable : affecte le vendeur indiqué au lead courant.
 */
export function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Jeton non éditable d'une variable de gabarit (éditeurs d'email et de SMS), avec son bouton de suppression.
 * @param {string} name Nom de la variable, posé en `data-variable`.
 * @returns {string}
 */
export function variableTokenHtml(name) {
    const safeName = escapeHtml(name);
    return `<span class="variable-token" contenteditable="false" data-variable="${safeName}">${safeName}`
        + `<span class="delete-btn" title="${escapeHtml(translate('app.common.delete_variable'))}">&times;</span></span>`;
}

/**
 * Ouvre un lien Calendly (généré par le filtre Twig `calendlyButton`) avec les
 * coordonnées du prospect pré-remplies. Namespace `.calendly` : rebind sans
 * doublon après rafraîchissement AJAX de la liste vendeurs.
 */
export function bindCalendlyPrefillLinks()
{
    $(document).off('click.calendly auxclick.calendly').on('click.calendly auxclick.calendly', '.calendly-prefill-link', function(event) {
        if (event.type === 'auxclick' && event.which !== 2) return;
        event.preventDefault();
        event.stopPropagation();

        const baseUrl = $(this).data('calendlyUrl');
        if (!baseUrl) return;

        const firstName = ($('#lead_customer_firstName').val() || '').trim();
        const lastName  = ($('#lead_customer_lastName').val()  || '').trim();
        const email     = ($('#lead_customer_email').val()     || '').trim();
        // Dataset natif plutôt que jQuery.data() : ce dernier cache la première
        // lecture et ignorerait une re-saisie du numéro par l'opérateur.
        const phoneInput = document.getElementById('lead_customer_phoneNumber');
        const phoneE164  = phoneInput?.dataset.e164 || '';

        const fullName = [firstName, lastName].filter(Boolean).join(' ');

        const params = new URLSearchParams();
        if (fullName) params.set('name', fullName);
        if (firstName) params.set('first_name', firstName);
        if (lastName) params.set('last_name', lastName);
        if (email) params.set('email', email);
        if (phoneE164) {
            // Calendly expose le téléphone sous deux paramètres selon la config
            // de l'événement : `phone_number` (SMS de rappel) et `a1` (première
            // question custom). On envoie les deux, les inconnus sont ignorés.
            params.set('phone_number', phoneE164);
            params.set('a1', phoneE164);
        }

        const separator = baseUrl.includes('?') ? '&' : '?';
        const finalUrl  = params.toString() ? baseUrl + separator + params.toString() : baseUrl;

        window.open(finalUrl, '_blank', 'noopener');
    });
}

export function loadSellerHints()
{
    const $hints = $('#seller-hints');
    if ($hints.length === 0) return;
    if (pageConfig().pageType === 'edit') return;
    // Indices cliquables = réaffectation, sans objet sur un lead verrouillé
    if (isLeadLocked()) return;

    const posId    = $('#lead_pos').val();
    const phone1   = $('#lead_customer_phoneNumber').val();
    const phone2   = $('#lead_customer_otherPhoneNumber').val();
    const email    = $('#lead_customer_email').val();
    const leadId   = $('#value_lead_id').val();
    const interest = getInterest() ?? '';
    const $referenceField = $('.request-reference:visible');
    const reference       = $referenceField.find('input').val() ?? '';
    const referenceLabel  = $referenceField.find('input').attr('placeholder') ?? '';

    if (!posId) return;

    $.get(Routing.generate('lead_seller_hints', {'pos_id': posId}), {
        reference: reference,
        phone1: phone1,
        phone2: phone2,
        email: email,
        lead: leadId,
        interest: interest
    }, function (data) {
        let html = '';

        if (data.referenceHint) {
            const h = data.referenceHint;
            const safeReference   = escapeHtml(h.reference);
            const safeAlias       = escapeHtml(h.sellerAlias);
            const labelPrefix     = referenceLabel ? referenceLabel + ' ' : '';
            html += '<div class="seller-hint --reference">'
                + labelPrefix + '"<b>'
                + safeReference + '</b>" ' + translate('app.lead.hint_reference_assigned_to', {}, 'validators') + ' '
                + '<span class="seller-hint__alias --tooltip-txt" data-title="' + escapeHtml(translate('app.lead.hint_assign_to', {'seller': h.sellerAlias}, 'validators')) + '" data-seller-id="' + escapeHtml(String(h.sellerId)) + '">' + safeAlias + '</span>'
                + '</div>';
        }

        if (data.prospectHint) {
            const h = data.prospectHint;
            const safeTitle       = escapeHtml(h.title);
            const safeName        = escapeHtml(h.name);
            const safeAlias       = escapeHtml(h.sellerAlias);
            html += '<div class="seller-hint --prospect">'
                + (h.title ? safeTitle + ' ' : '') + safeName
                + ' (' + escapeHtml(h.interest) + ' #' + escapeHtml(String(h.leadId)) + ') ' + translate('app.lead.hint_prospect_in_contact', {}, 'validators') + ' '
                + '<span class="seller-hint__alias --tooltip-txt" data-title="' + escapeHtml(translate('app.lead.hint_assign_to', {'seller': h.sellerAlias}, 'validators')) + '" data-seller-id="' + escapeHtml(String(h.sellerId)) + '">' + safeAlias + '</span>'
                + '</div>';
        }

        (data.dutyHints || []).forEach(function (h) {
            const sellerSpans = (h.sellers || []).map(function (s) {
                const safeAlias = escapeHtml(s.sellerAlias);
                return '<span class="seller-hint__alias --tooltip-txt" data-title="' + escapeHtml(translate('app.lead.hint_assign_to', {'seller': s.sellerAlias}, 'validators')) + '" data-seller-id="' + escapeHtml(String(s.sellerId)) + '" data-seller-pos-id="' + escapeHtml(String(s.sellerPosId)) + '">' + safeAlias + '</span>';
            }).join(', ');
            html += '<div class="seller-hint --duty">'
                + escapeHtml(translate('app.lead.hint_duty', {'pos': h.posName}, 'validators')) + ' '
                + sellerSpans
                + '</div>';
        });

        if (html) {
            $hints.html(html).removeClass('--hidden');
            launchTooltip();
            $hints.find('.seller-hint__alias').on('click', function () {
                const sellerId = $(this).data('seller-id');
                const sellerPosId = $(this).data('seller-pos-id');
                // Un même vendeur peut avoir plusieurs lignes (native + permanence(s)) partageant
                // le même data-id : cibler aussi data-pos pour ne sélectionner que la ligne visée
                // par ce hint, pas toutes les occurrences de ce vendeur.
                let $sellerLine = sellerPosId !== undefined
                    ? $('.seller-line[data-id="' + sellerId + '"][data-pos="' + sellerPosId + '"]')
                    : $();
                if (!$sellerLine.length) {
                    $sellerLine = $('.seller-line[data-id="' + sellerId + '"]').first();
                }
                if ($sellerLine.length) {
                    // Un vendeur de permanence est déjà remonté en tête par le tri serveur
                    // (onDutyPriority) : ne pas le redéplacer au clic, contrairement aux hints
                    // référence/prospect qui remontent la ligne pour la rendre visible.
                    const isDutyHint = $(this).closest('.seller-hint').hasClass('--duty');
                    toggleSellerLineSelection($sellerLine, !isDutyHint);
                }
            });
        } else {
            $hints.addClass('--hidden').html('');
        }
    });
}

export function sortingSellersHandler()
{
    const sortButtons = $('.sellers .actions span');
    sortButtons.on('click', function() {
        sortButtons.removeClass('--current');
        $(this).addClass('--current');
        const direction = $(this).data('direction');
        const container = $('.sellers .content');
        const searchable = $(this).data('col');
        $('.seller-line').sort(function (a, b) {
            const contentA = $(a).find('.seller-line__' + searchable).data('sort');
            const contentB = $(b).find('.seller-line__' + searchable).data('sort');
            if (direction === 'asc') {
                return (contentA < contentB) ? -1 : (contentA > contentB) ? 1 : 0;
            } else {
                return (contentB < contentA) ? -1 : (contentB > contentA) ? 1 : 0;

            }
        }).appendTo(container);
    })
}

export function autoSelectSeller()
{

    const sellers = $('.sellers .content');


    const assignmentType = $('.sellers').data('assignment');
    if (assignmentType === 'prioritization') {
        $('[data-col="position"][data-direction="asc"]').trigger('click');
    } else {
        $('[data-col="last_lead"][data-direction="asc"]').trigger('click');
    }
    setTimeout(function() {
        // les absents doivent être en bas de liste sauf pour la prioritization
        // ordre voulu dans la zone "absents" : hebdomadaires (orange) d'abord, ponctuels (rouge) en dernier
        if (assignmentType !== 'prioritization' || $('.sellers').hasClass('--group') === false) {
            $('.seller-line.absent.absent--weekly:not(.seller-selected)').appendTo(sellers);
            $('.seller-line.absent:not(.absent--weekly):not(.seller-selected)').appendTo(sellers);
        } else {
            // pour la prioritization dans le cas d'un groupe,
            // on met les sellers absents du groupe avant les sellers de PDV
            let lastGroupSeller = $('.seller-line.--with-position:last');
            // insertAfter empile en sens inverse : insérer les ponctuels en premier pour qu'ils finissent en dessous des hebdos
            $('.seller-line.absent.--with-position:not(.absent--weekly):not(.seller-selected)').insertAfter(lastGroupSeller);
            $('.seller-line.absent.--with-position.absent--weekly:not(.seller-selected)').insertAfter(lastGroupSeller);
            $('.seller-line.absent:not(.--with-position).absent--weekly:not(.seller-selected)').appendTo(sellers);
            $('.seller-line.absent:not(.--with-position):not(.absent--weekly):not(.seller-selected)').appendTo(sellers);
        }
        setTimeout(function() {
            // tous les vendeurs en permanence remontent en haut (même s'ils ne sont pas
            // celui présélectionné), triés à tour de rôle entre eux (comme l'affectation Inturn :
            // date de dernier lead la plus ancienne d'abord), indépendamment du mode d'affectation
            // configuré sur l'univers — qui continue de s'appliquer normalement aux autres vendeurs.
            $(sortSellerLinesByTurn($('.seller-line.--on-duty'))).prependTo(sellers);
            // le vendeur réellement sélectionné passe devant les autres vendeurs de permanence
            $('.seller-line.seller-selected').prependTo(sellers);
        }, 5);
    }, 50);


    const { pageType } = pageConfig();
    if (pageType !== undefined && !isLeadLocked()) {
        if (pageType === "mail" || pageType === "new") {

            const leadZip = $('.lead-zip');
            const leadOrigin = $('#lead_origin');
            const requestReference = $('.request-reference').find('input');
            requestReference.unbind('input');
            // le vendeur de permanence est toujours prioritaire sur le mode d'affectation configuré
            if (autoSelectSellerByDuty()) {
                return;
            }
            const assignmentType = $('.sellers').data('assignment');
            if (assignmentType === 'zipcode') {
                autoSelectSellerByZipcode(leadZip.val());
                leadZip.change(function () {
                    autoSelectSellerByZipcode($(this).val());
                });
            }
            else if (assignmentType === 'origin') {
                autoSelectSellerByOrigin(leadOrigin.find('option:selected').text());
                leadOrigin.change(function () {
                    autoSelectSellerByOrigin($(this).find('option:selected').text());
                });
            }
            else if (assignmentType === 'affectcode') {
                autoSelectSellerByAffectcode(requestReference.val());
                requestReference.on('input', function () {
                    autoSelectSellerByAffectcode($(this).val());
                });
            }
            else if (assignmentType === 'inturn') {
                // trier par date décroissante
                autoSelectSellerByInturn();
            }
            else if (assignmentType === 'prioritization') {
                autoSelectSellerByPrioritization();
            }
        }
    }
}

export function preSelectSellerLine()
{
    let chosenSellerId = parseInt($('#lead_seller').find('option:selected').val());
    let posId = parseInt($('#lead_ownerGroup').find('option:selected').val());
    if (chosenSellerId != null) {
        $('.seller-line').each(function(){
            if ( $(this).data("id") === chosenSellerId && $(this).data("pos") === posId ) {
                toggleSellerLineSelection($(this));
            }
        })
    }
}

function disableFields(field)
{
    let sellerLine = field.closest('.seller-line');

    $('#lead_seller').find('option:selected').prop('selected', false);

    field.prop('checked', false);
    sellerLine.removeClass("seller-selected");
}
function selectLeadPos()
{
    let sellerPosId = $('#selectPos').find('option:selected').val();
    $('#lead_ownerGroup option[value=' + sellerPosId + ']').prop('selected', true);
}

export function toggleSellerLineSelection(select, move) {
    let checkbox = select.find(".select-lead-seller-checkbox");
    checkbox.trigger('click');

    let sellerLine = select.closest('.seller-line');
    let chosenSellerId = sellerLine.data('id');
    let sellerPos = sellerLine.data('pos')

    // Disable previous seller selected
    $('.select-lead-seller-checkbox').each(function(){
        let sellerLineIdValue = $(this).closest('.seller-line').data('id');
        let sellerLinePosValue = $(this).closest('.seller-line').data('pos')
        if (
            ( sellerLineIdValue !== chosenSellerId ) ||
            ( sellerLineIdValue === chosenSellerId && sellerLinePosValue !== sellerPos )
        ) {
            disableFields($(this));
        }
    });
    $('#lead_seller option[value=' + chosenSellerId + ']').prop('selected', true).trigger('change');
    $('#lead_ownerGroup option[value=' + sellerPos + ']').prop('selected', true);
    sellerLine.addClass("seller-selected");
    if (typeof move !== 'undefined' && move) {
        sellerLine.prependTo(".sellers .content");
    }
    selectLeadPos();
    // dataset (chaîne) contre data() (nombre).
    if (String(pageConfig().sellerId) !== String(chosenSellerId) && sellerLine.hasClass('absent')) {
        window.sellerPresent = false;
        window.sellerIdentity = sellerLine.find('.seller-identity').text();
    } else {
        window.sellerPresent = true;
    }

}

export function toggleSectionContentVisibility()
{
    $('.toggle-section-visibility').unbind('click').click(function() {
        const sectionContent = $(this).closest('header').siblings('.section-content');
        const isReduced = sectionContent.hasClass('--reduced-active') || sectionContent.hasClass('--reduced-completed');

        if ($(this).hasClass('--close')) {
            $(this).removeClass('--close');
            if (isReduced) {
                sectionContent.removeClass('--reduced');
            } else {
                sectionContent.removeClass('--hidden');
            }
            if ($(this).hasClass('--stats')) {
                setCookie("statsFilterClosed", 0, 7);
            }
            $(this).parents('.section').find('#transferRate2').addClass('--hidden');
        } else {
            $(this).addClass('--close');
            if (isReduced) {
                sectionContent.addClass('--reduced');
            } else {
                sectionContent.addClass('--hidden');
            }
            if ($(this).hasClass('--stats')) {
                setCookie("statsFilterClosed", 1, 7);
            }
            $(this).parents('.section').find('#transferRate2').removeClass('--hidden');
        }
        const toggleOtherClass = $(this).data('toggle-other');
        if (toggleOtherClass) {
            const otherSections = $('.section.' + toggleOtherClass);
            otherSections.each(function () {
                $(this).toggleClass('--hidden');
            });
        }

    });

    $('.section-content.--mobile-hidden').each(function() {
        const toggleButton = $(this).parents('.section').find('.toggle-section-visibility');

        if (window.matchMedia('(max-width: 1024px)').matches) {
            $(this).addClass('--hidden').removeClass('--mobile-hidden');
            toggleButton.addClass('--close').removeClass('--mobile-close');
        }
    });
}

function autoSelectSellerByZipcode(zipcode) {
    //check univers (car plus de unbind)
    const assignmentType = $('.sellers').data('assignment');
    if (assignmentType !== 'zipcode') {
        return;
    }
    if (zipcode === '') {
        return;
    }
    let sellerLine = $('.seller-line');
    let lastSellerAssignLeadDate = new Date();
    let sellerToSelect;

    sellerLine.each(function () {
        // Comparaison tag par tag : le texte concaténé des tags créait des faux positifs ("75001"+"75002" contient "17500")
        const sectors = $(this).find('.seller-sector .tag').map((i, el) => el.textContent.replace(/\s+/g, '')).get();
        if (sectors.some(sector => sector.includes(zipcode))) {
            let sellerAssignLeadDate = $(this).find('.seller-last-assign-lead-date').text().trim();
            let numbers = sellerAssignLeadDate.match(/\d+/g);
            // si pas de date, on l'assigne en priorité
            if (numbers === null) {
                sellerToSelect = $(this);
                return false;
            }
            let date = new Date(numbers[2], numbers[1]-1, numbers[0], numbers[3], numbers[4]);
            if (date < lastSellerAssignLeadDate) {
                sellerToSelect = $(this);
                return false;
            }
        }
    });
    if (typeof sellerToSelect !== 'undefined') {
        toggleSellerLineSelection(sellerToSelect, true);
    }
}
function autoSelectSellerByOrigin(origin) {
    //check univers (car plus de unbind)
    const assignmentType = $('.sellers').data('assignment');
    if (assignmentType !== 'origin') {
        return;
    }
    if (origin === '') {
        return;
    }
    let sellerLine = $('.seller-line');
    let lastSellerAssignLeadDate = new Date();
    let sellerToSelect;

    sellerLine.each(function () {
        let sellerLineOrigin = $(this).find('.seller-origin').text();
        if (sellerLineOrigin.includes(origin)) {
            let sellerAssignLeadDate = $(this).find('.seller-last-assign-lead-date').text().trim();
            let numbers = sellerAssignLeadDate.match(/\d+/g);
            // si pas de date, on l'assigne en priorité
            if (numbers === null) {
                sellerToSelect = $(this);
                return false;
            }
            let date = new Date(numbers[2], numbers[1]-1, numbers[0], numbers[3], numbers[4]);
            if (date < lastSellerAssignLeadDate) {
                sellerToSelect = $(this);
                return false;
            }
        }
    });
    if (typeof sellerToSelect !== 'undefined') {
        toggleSellerLineSelection(sellerToSelect, true);
    }
}

function autoSelectSellerByAffectcode(reference) {
    if (reference === '') {
        return;
    }

    let bestMatch = null;
    let bestMatchLength = 0;

    $('.seller-line').each(function () {
        let sellerLineReference = $(this).find('.seller-affect-code').text().replace(/\s+/g, '');
        if (reference.toUpperCase().indexOf(sellerLineReference.toUpperCase()) !== -1) {
            if (sellerLineReference.length > bestMatchLength) {
                bestMatch = $(this);
                bestMatchLength = sellerLineReference.length;
            }
        }
    });

    if (bestMatch) {
        toggleSellerLineSelection(bestMatch, true);
    }
}

// Trie des .seller-line par tour de rôle façon affectation Inturn : date de dernier lead
// la plus ancienne d'abord (les jamais-affectés, data-sort absent/"0", passent en premier).
function sortSellerLinesByTurn(lines) {
    return lines.get().sort(function (a, b) {
        const sortA = parseInt($(a).find('.seller-line__last_lead').data('sort'), 10) || 0;
        const sortB = parseInt($(b).find('.seller-line__last_lead').data('sort'), 10) || 0;
        return sortA - sortB;
    });
}

function autoSelectSellerByDuty() {
    const dutyLines = $('.seller-line.--on-duty');
    if (!dutyLines.length) {
        return false;
    }
    // à tour de rôle entre les vendeurs de permanence, indépendamment du mode d'affectation
    // configuré sur l'univers.
    const nextInTurn = sortSellerLinesByTurn(dutyLines)[0];
    toggleSellerLineSelection($(nextInTurn), true);
    return true;
}

function autoSelectSellerByInturn() {
    toggleSellerLineSelection($('.seller-line:first'));
    return false;
}
function autoSelectSellerByPrioritization() {
    toggleSellerLineSelection($('.seller-line:not(.absent):first'));
    return false;
}

/**
 * Met en gras, dans la section équipe, les tags (initiales, secteur, origine) qui
 * correspondent aux valeurs saisies sur le lead : le secteur est un préfixe du code postal
 * (ex : "75" couvre 75013, plus large que l'auto-sélection qui exige le code complet),
 * les initiales sont contenues dans la référence du bien, l'origine est comparée au libellé.
 */
function highlightMatchingSellerTags() {
    const zipcodes = $('.lead-zip').map((i, el) => ($(el).val() || '').replace(/\s+/g, '')).get().filter(Boolean);
    const references = $('.request-reference input').map((i, el) => ($(el).val() || '').replace(/\s+/g, '').toUpperCase()).get().filter(Boolean);
    const origin = $('#lead_origin').val() ? $('#lead_origin option:selected').text().trim() : '';

    $('.seller-sector .tag').each(function () {
        const sector = $(this).text().replace(/\s+/g, '');
        $(this).toggleClass('--match', sector !== '' && zipcodes.some(zipcode => zipcode.startsWith(sector)));
    });
    $('.seller-affect-code .tag').each(function () {
        const code = $(this).text().replace(/\s+/g, '').toUpperCase();
        $(this).toggleClass('--match', code !== '' && references.some(reference => reference.includes(code)));
    });
    $('.seller-origin .tag').each(function () {
        $(this).toggleClass('--match', origin !== '' && $(this).text().trim() === origin);
    });

    // Tags correspondants en tête puis ordre alphabétique (tri recalculé à chaque saisie pour qu'un tag qui ne correspond plus reprenne sa place)
    $('.seller-sector, .seller-affect-code, .seller-origin').each(function () {
        const tags = $(this).children('.tag').get().sort((a, b) =>
            $(b).hasClass('--match') - $(a).hasClass('--match')
            || a.textContent.trim().localeCompare(b.textContent.trim(), 'fr', {numeric: true, sensitivity: 'base'})
        );
        $(this).append(tags);
    });

    refreshSellerTagsOverflow();
}

const SELLER_TAGS_VISIBLE_LIMIT = 2;

/**
 * Replie les colonnes de tags trop longues derrière un bouton « +N ».
 * Les tags qui correspondent au lead (--match) ou à la recherche en cours restent toujours visibles,
 * et on ne replie pas pour un seul tag (un « +1 » ne fait rien gagner).
 */
export function refreshSellerTagsOverflow() {
    $('.seller-sector, .seller-affect-code, .seller-origin').each(function () {
        const column = $(this);
        const isSearched = column.hasClass('--search-active');
        const foldableTags = column.children('.tag').filter(index => index >= SELLER_TAGS_VISIBLE_LIMIT).filter(function () {
            return !$(this).hasClass('--match') && !(isSearched && !$(this).hasClass('--search-dimmed'));
        });
        const isFoldable = foldableTags.length > 1;
        const isExpanded = column.hasClass('--expanded');

        column.children('.tag').removeClass('--overflow');
        if (isFoldable && !isExpanded) {
            foldableTags.addClass('--overflow');
        }

        // Libellé "+N" / "−" posé en CSS (attr) : il ne pollue pas le texte lu par la recherche et l'auto-sélection
        let moreButton = column.children('.tag-more');
        if (!isFoldable) {
            moreButton.remove();
            return;
        }
        if (moreButton.length === 0) {
            moreButton = $('<button type="button" class="tag-more"></button>');
        }
        // En tête pour que le float le place en haut à droite (le tri ajoute les tags à la suite)
        column.prepend(moreButton.attr('data-count', foldableTags.length));
    });
}

function bindSellerTagHighlight() {
    highlightMatchingSellerTags();
    // Délégué au document : les champs de demande sont re-rendus au changement d'intérêt
    $(document)
        .off('.sellerTagHighlight')
        .on('input.sellerTagHighlight change.sellerTagHighlight', '.lead-zip, .request-reference input, #lead_origin', highlightMatchingSellerTags);
}

/**
 * Rafraîchit l'alias opérateur des lignes affichées après un push `xxxListOperators`
 * (emails, JDA, campagnes : même contrat JSON, cf. ListOperatorInfo côté serveur).
 * Les ids sont relus dans le DOM à chaque appel : pagination et pushs remplacent les lignes.
 *
 * L'URL est portée par la table elle-même (`data-operator-info-url`), ce qui
 * survit aux rechargements AJAX partiels ; absente, la fonction ne fait rien.
 *
 * @param {string}           tableSelector
 * @param {string}           payloadKey
 * @param {string}           aliasSelector élément de la ligne portant l'alias
 */
function updateLinesOperatorInfo(tableSelector, payloadKey, aliasSelector)
{
    const url = $(tableSelector).data('operator-info-url');
    if (typeof url === 'undefined') return;

    const ids = $(tableSelector + ' .tableLine .select-line').map(function(){
        return $(this).data('id');
    }).get();
    if (ids.length === 0) return;

    $.post(url, JSON.stringify({ [payloadKey]: ids }))
        .done(function(operatorInfos){
            $.each(operatorInfos, function(id, operatorInfo){
                $(tableSelector + " .select-line[data-id='" + id + "']").closest('.tableLine').find(aliasSelector)
                    .text(operatorInfo.alias)
                    .toggleClass('--current-operator', operatorInfo.currentOperator === true);
            });
        })
        .fail(function(jqXHR) {
            reportError(payloadKey + ' operator-info XHR failed', jqXHR, { status: jqXHR.status, payloadKey: payloadKey });
        });
}

export function updateEmailLineInfoOperator()
{
    updateLinesOperatorInfo('.email-table', 'listEmailsIds', '.tableLine-alias');
}

export function updateCallLineInfoOperator()
{
    updateLinesOperatorInfo('.jda-table', 'listCallsIds', '.call-info-operator p');
}

export function updateContactLineInfoOperator()
{
    updateLinesOperatorInfo('.contact-table', 'listContactsIds', '.contact-info-operator');
}

/**
 * Lie le bouton d'anonymisation d'une ligne de résultat de recherche : confirmation, POST sur
 * `data-url`, puis retrait de la ligne et message de succès.
 */
function bindAnonymization(selector, confirmKey, errorLabel)
{
    $(selector).unbind('click').click(function(e){
        e.preventDefault();
        const url = $(this).data('url');
        const id = $(this).data('id');
        const $line = $(this).closest('tr');
        openConfirmation({'title': translate('app.common.confirmation'), 'confirmText': translate('app.common.validate'), 'text': translate(confirmKey, {'id': id, 'customer': $(this).attr('data-customer')})});
        $('.pop-confirmation .confirm').on('click', function() {
            $.post(url).done(function(response) {
                $line.remove();
                $('.js-flash-message-success .text').text(response.message);
                $('.js-flash-message-success').removeClass('--hidden');
                setTimeout(function() {
                    $('.js-flash-message-success').addClass('--hidden');
                }, 15000);
            }).fail(function(jqXHR) {
                reportError(errorLabel, jqXHR, { status: jqXHR.status, id: id });
                openConfirmation({'text': translate('app.common.error'), 'confirmText': translate('app.common.close'), 'alert': true});
            });
        });
    });
}

export function validateLeadAnonymization()
{
    bindAnonymization('.validate-anonymization', 'app.common.confirm_anonymize_lead', 'Anonymisation du lead impossible');
}

export function validateContactAnonymization()
{
    bindAnonymization('.validate-contact-anonymization', 'app.common.confirm_anonymize_contact', 'Anonymisation du contact impossible');
}


/**
 * Ouvre la popin de confirmation commune.
 * `text` est inséré en texte brut : il porte souvent un nom saisi (client, PDV, vendeur).
 * `html: true` l'insère en HTML, réservé à un contenu de confiance (rendu serveur, ou
 * construit en JS avec chaque donnée passée par escapeHtml()).
 */
export function openConfirmation(params)
{
    const popOverlay = $('body > .pop-overlay');
    const popConfirmation = $('body > .pop-confirmation');
    // Bouton supplémentaire d'un contenu précédent (.pop-button, cf. mode `content`) : propre à ce contenu
    popConfirmation.find('.buttons .pop-button').remove();
    popConfirmation.removeClass();
    popConfirmation.addClass('pop-confirmation');
    popConfirmation.addClass('section');
    if (params.class) {
        popConfirmation.addClass(params.class);
    }
    popOverlay.removeClass('--hidden');
    // `text` absent : la zone garde son contenu (loader du mode `content`).
    if (params.text !== undefined) {
        const $text = popConfirmation.find('.text');
        if (params.html === true) {
            $text.html(params.text);
        } else {
            $text.text(params.text);
        }
    }
    if (params.element) {
        popConfirmation.html(params.element.html());
    } else if (params.content) {
        popConfirmation.find('.text .common-loader').removeClass('--hidden');
        $.get(params.content, function (data) {
            popConfirmation.find('.text').html(data);
            // .pop-button du contenu chargé : rangé avec Valider / Annuler, sur la même ligne
            popConfirmation.find('.text .pop-button').appendTo(popConfirmation.find('.buttons'));
            if (params.afterOpen) {
                params.afterOpen();
            }
        }).fail(function (jqXHR) {
            // Sans ce message la popin resterait sur son loader (ou sur le contenu précédent) sans rien dire.
            reportError('popin content XHR failed', jqXHR, { status: jqXHR.status, url: params.content });
            popConfirmation.find('.text .common-loader').addClass('--hidden');
            popConfirmation.find('.text').text(translate('app.common.error'));
        });
    } else {
        if (params.title) {
            popConfirmation.find('h3').text(params.title).removeClass('--hidden');
        } else {
            popConfirmation.find('h3').html('').addClass('--hidden');
        }
        if (params.cancelText) {
            popConfirmation.find('.cancel').text(params.cancelText);
        }
    }
    if (params.confirmText) {
        popConfirmation.find('.confirm').text(params.confirmText);
    }

    if (params.alert) {
        popConfirmation.find('.cancel').addClass('--hidden');
        // si c'est une alerte, on affiche uniquement le bouton "confirmer" qui sert à fermer la pop
        popConfirmation.find('.confirm').unbind('click').on('click', function(event) {
            event.preventDefault();
            popOverlay.addClass('--hidden');
            popConfirmation.addClass('--hidden');
        });
    } else {
        popConfirmation.find('.cancel').removeClass('--hidden');
        // sinon on affiche "confirmer" et annuler". "annuler" sert à fermer la pop
        popConfirmation.find('.confirm').unbind('click').on('click', function(event) {
            event.preventDefault();
            if (params.redirectUrl) {
                window.location = params.redirectUrl;
            }
            else if (params.form) {
                if (params.data) {
                    // si c'est un formulaire, confirmer sert à submit le form (en modifiant la variable qui bloquait le submit)
                    params.data.valueOf = params.data.toSource = params.data.toString = function(){ return true; };
                }
                params.form.submit();
            } else if (params.action) {
                params.action(params.param);
            } else if (params.button) {
                params.button.attr('data-popin', 1);
                params.button.trigger('click');
            } else {
                // sinon, "confirmer" sert à rediriger vers l'url initiale
                popConfirmation.find('.confirm').attr('data-url', params.url);
            }
            if (!params.keepOpenOnConfirm) {
                popOverlay.addClass('--hidden');
                popConfirmation.addClass('--hidden');
            }
            if (params.afterClose) {
                params.afterClose();
            }
        });
        popConfirmation.find('.cancel').unbind('click').on('click', function(event) {
            event.preventDefault();
            popOverlay.addClass('--hidden');
            popConfirmation.addClass('--hidden');
            if (params.afterClose) {
                params.afterClose();
            }
        });
    }
    popConfirmation.removeClass('--hidden');
}

export function handleSendReminder() {
    $('.send-reminder').unbind('click').on('click', function(event) {
        event.preventDefault();
        $(this).addClass('--disabled');
        $.post($(this).attr('href'), function (data) {
            if (data.ok === true) {
                openConfirmation({'text': data.message, 'confirmText': translate('app.common.close'), 'alert': true});
            } else {
                openConfirmation({'text': data.message, 'confirmText': translate('app.common.close'), 'alert': true});
            }
        }).fail(function() {
            openConfirmation({'text': translate('app.reminder.not_sent'), 'confirmText': translate('app.common.close'), 'alert': true});
        })
    })
}
function handlePopinLeadForm(element) {
    if (element.length === 0) {
        return;
    }
    element.find('form').on('submit', function(event) {
        event.preventDefault();
        $.ajax({
            url: $(this).attr('action'),
            type: $(this).attr('method'),
            data: $(this).serialize(),
        })
        .done(function( data ) {
            if (data.url) {
                window.location.href = data.url;
            }
            if (data.result === 'success') {
                if (data.reload) {
                    location.reload();
                    return false;
                }
                $('.custom-popin').remove();
                $('.opacity-layer').remove();
                openConfirmation({'text': data.message, 'confirmText': translate('app.common.close'), 'alert': true});
                // reaffectation : on met à jour le seller et le pdv
                if (element.attr('id') === 'reaffect') {
                    if ($('.leads-table').length > 0) {
                        // update lead
                        $('.leads-table tr[data-id='+data.id+']').find('.pos-seller').empty().append(
                            document.createTextNode(data.pos),
                            $('<span class="seller-alias"></span>').text(data.seller)
                        );
                    }
                    const listNoteWrapper = $('.lead-note-form .lead-note-list-wrapper');
                    if (listNoteWrapper.length > 0) {
                        const loaderWrapper = $('.lead-note-form .common-loader');
                        // update notes
                        listNoteWrapper.html('');
                        loaderWrapper.removeClass('--hidden');
                        $.get( Routing.generate('notes_list', {'id': data.id}))
                            .done(function( response ) {
                                listNoteWrapper.html(response.template);
                                loaderWrapper.addClass('--hidden');
                            })
                            .fail(function (xhr) {
                                // Lead réaffecté hors du périmètre de l'utilisateur : notes refusées (403).
                                console.error('notes_list: HTTP ' + xhr.status);
                                loaderWrapper.addClass('--hidden');
                                listNoteWrapper.append($('<div class="empty-data">').text(translate('app.error.generic')));
                            });
                    }
                } else {
                    //sinon on reloade
                    if ($('.leads-table').length > 0) {
                        let url = window.location.href;
                        let pageNb = $('#currentPage').val();
                        // si on supprime le dernier de la liste et qu'on est pas sur la page 1
                        if (pageNb > 1 && $('.leads-table table tr').length <= 2)  {
                            pageNb = pageNb - 1;
                        }
                        let pageParam = 'page=' + pageNb;
                        url = url + (url.includes('?') ? '&' : '?') + pageParam;
                        $.get(url)
                            .done(function( response ) {
                                $('.leads-table').html(response);
                                handleHomePaginationLinkAction();
                                handlePopinAction();
                            });
                    }
                }
            } else {
                element.replaceWith(data);
                handlePopinAction();
                handleChangePos();
                handlePopinLeadForm($('#reaffect'));
                handlePopinLeadForm($('#lostDismiss'));
                handlePopinLeadForm($('#lostValidate'));
            }
        })
        .fail(function(jqXHR) {
            reportError('popin lead form XHR failed', jqXHR, { status: jqXHR.status });
        })
    })
}

function preventDoublon()
{
    $('form.preventDoublon').unbind('submit').submit(function() {
        //block multiple submit
        if (typeof $(this).data('blocked') == "undefined" || $(this).data('blocked') === false) {
            $(this).data('blocked',true);
            return true;
        } else {
            return false;
        }
    });
}

export function setCookie(name, value, days) {
    let expires = "";
    if (days) {
        let date = new Date();
        date.setTime(date.getTime() + (days*24*60*60*1000));
        expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "")  + expires + "; path=/";
}

/**
 * Échec d'un ajout de note (rappel, relance email) : remet les notes retirées pendant le chargement
 * et affiche au-dessus le message porté par le bouton. 403/404 = lead réaffecté hors périmètre ou
 * supprimé pendant que la fiche était ouverte : réessayer ne changerait rien, d'où un message dédié.
 */
export function showLeadNoteActionError(xhr, button, listNoteWrapper, previousNotes) {
    const messageKey = { 403: 'errorDenied', 404: 'errorNotFound' }[xhr.status] ?? 'error';
    listNoteWrapper.empty().append(previousNotes);
    listNoteWrapper.prepend($('<div class="empty-data">').text(button.data(messageKey)));
}

export function translate(key, parameters = {}, domain = null, locale = null) {
    if (typeof Translator !== 'undefined' && typeof Translator.trans === 'function') {
        return Translator.trans(key, parameters, domain, locale);
    } else {
        console.warn(`Translator or Translator.trans not available for key: ${key}`);
        return key;
    }
}

export function checkPhoneFormat(number)
{
    return /^(\+?\d{4,17})$/.test(number);
}
export function showError(message, element, custom = false)
{
    removeError(element);

    let classes = "errors";
    if (custom) {
        classes += " --custom";
    }
    element.addClass('--warning');
    if (element.hasClass('select2-hidden-accessible')) {
        element.next('.select2').addClass('--warning');
    }

    let target = getTarget(element);

    target.after($('<ul></ul>').addClass(classes).append($('<li></li>').text(message)));
}

export function removeError(element)
{
    // Un email jugé invalide par email-detector.js garde son `--warning` (picto toujours affiché,
    // et form-lead.js s'appuie sur cette classe pour bloquer l'envoi).
    element.not('[data-email-invalid]').removeClass('--warning');
    if (element.hasClass('select2-hidden-accessible')) {
        element.next('.select2').removeClass('--warning');
    }
    let target = getTarget(element);

    target.next('ul.errors').remove();
}

export function getTarget(element)
{
    let target = element;

    // Cas phone-wrapper
    const $phoneWrapper = element.closest('.phone-field-wrapper');
    if ($phoneWrapper.length) {
        target = $phoneWrapper;
    }

    // Cas name-wrapper (nom / prénom du lead) et email-wrapper : l'erreur se place sous le wrapper, pas dedans
    const $inlineWrapper = element.closest('.name-wrapper, .email-wrapper');
    if ($inlineWrapper.length) {
        target = $inlineWrapper;
    }

    // Cas input-suffix-wrapper
    const $suffixWrapper = element.closest('.input-suffix-wrapper');
    if ($suffixWrapper.length) {
        const $parent = element.closest('.form-line');
        if ($parent.length) {
            target = $suffixWrapper;
        }
    }

    // Cas Select2
    if (element.hasClass('select2-hidden-accessible')) {
        const $select2 = element.next('.select2');
        if ($select2.length) {
            target = $select2;
        }
    }

    return target;
}

export function phoneFormat(element, force = false)
{
    const { pageType } = pageConfig();
    if ((pageType === "mail" || pageType === "new") || force){
        const number = $(element).val();
        if (number === '') {
            return true;
        }
        if (checkPhoneFormat(number) === false) {
            return false;
        }
    }
    return true;
}
window.phoneFormat = phoneFormat;

const DEFAULT_SCHEDULE_RANGE = {start: 8, end: 19};

/**
 * Heures couvertes par un planning .schedule : data-range-start / data-range-end posés par le template
 * (cf. PointOfSale::scheduleRange()), 8h-19h sans ces attributs.
 *
 * @param {jQuery} $schedule
 * @returns {{start: number, end: number}}
 */
export function scheduleRange($schedule) {
    const start = parseInt($schedule.attr('data-range-start'), 10);
    const end = parseInt($schedule.attr('data-range-end'), 10);
    return Number.isInteger(start) && Number.isInteger(end) && end > start ? {start, end} : DEFAULT_SCHEDULE_RANGE;
}

export function calculateStartPercentage(startHour, startMinute, range = DEFAULT_SCHEDULE_RANGE) {
    const startOfDay = range.start * 60;
    const endOfDay = range.end * 60;

    // Convertir l'heure de début en minutes depuis minuit
    const startTotalMinutes = (parseInt(startHour) * 60) + parseInt(startMinute);

    if (startTotalMinutes <= startOfDay) {
        return 0;
    }
    if (startTotalMinutes >= endOfDay) {
        return 100;
    }

    // Calculer le pourcentage en fonction de l'heure de début
    const percentage = ((startTotalMinutes - startOfDay) / (endOfDay - startOfDay)) * 100;
    return Math.round(percentage);
}

export function calculateDurationPercentage(startHour, startMinute, endHour = null, endMinute = null, range = DEFAULT_SCHEDULE_RANGE) {
    if (endHour === null) {
        endHour = startHour + 1;
    }
    if (endMinute === null) {
        endMinute = startMinute;
    }
    const startOfDay = range.start * 60;
    const endOfDay = range.end * 60;
    const totalDayDuration = endOfDay - startOfDay;

    // Convertir l'heure de début et l'heure de fin en minutes depuis minuit
    const startTotalMinutes = (parseInt(startHour) * 60) + parseInt(startMinute);
    const endTotalMinutes = (parseInt(endHour) * 60) + parseInt(endMinute);

    // Ramener le début et la fin dans la plage du planning
    const clampedStart = Math.max(startTotalMinutes, startOfDay);
    const clampedEnd = Math.min(endTotalMinutes, endOfDay);

    // Si l'heure de début est après l'heure de fin, retourner 0%
    if (clampedStart >= clampedEnd) {
        return 0;
    }

    // Calculer la durée en minutes entre l'heure de début et de fin
    const durationMinutes = clampedEnd - clampedStart;

    // Calculer le pourcentage par rapport à la durée totale du planning
    const percentage = (durationMinutes / totalDayDuration) * 100;

    return Math.round(percentage);
}

export function initAjaxSelect($select, route, tags = false, multiple = true) {
    $select.select2({
        tags: tags,
        multiple: multiple,
        ajax: {
            url: route,
            dataType: 'json',
            delay: 250,
            data: function(params) {
                return { q: params.term };
            },
            processResults: function(data) {
                return { results: data.items }; // data.items = [{id, text}, ...]
            }
        },
        // allowClear exige un placeholder (select2 4.1) : sans lui, la croix ne vide pas le champ
        placeholder: $select.data('placeholder') ?? '',
        allowClear: true
    });

    // Après « tout effacer », select2 rouvre la liste : on s'en tient à l'effacement.
    // Liste déjà ouverte : select2 la referme, rien à bloquer (sinon le clic suivant serait absorbé)
    $select.on('select2:clear', function () {
        if ($select.data('select2')?.isOpen()) {
            return;
        }
        $select.one('select2:opening', function (e) {
            e.preventDefault();
        });
    });

    // Ajout d'une option si elle n'existe pas déjà
    $select.on('select2:select', function(e) {
        let data = e.params.data;
        if (!$select.find(`option[value='${data.id}']`).length) {
            let newOption = new Option(data.text, data.id, true, true);
            $select.append(newOption).trigger('change');
        }
    });

    // Suppression de l'option quand elle est désélectionnée
    $select.on('select2:unselect', function(e) {
        let data = e.params.data;
        $select.find(`option[value='${data.id}']`).remove();
        $select.trigger('change');
    });

    // Met à jour Select2 (namespace .select2 pour resync UI sans déclencher les handlers
    // `change` génériques — notamment le marqueur "formulaire modifié" de admin.js)
    $select.trigger('change.select2');
}

export function checkErrors(check)
{
    let errors = 0;
    for (const value of Object.values(check)) {
        const element = $(value.element);

        element.each(function() {
            // on vérifie que l'élément est visible sinon il ne faut pas faire le test
            if ($(this).is(':visible')) {
                let test;
                if (value.function) {
                    test = window[value.function]($(this));
                } else {
                    test = $(this).val() !== '';
                }
                if (test !== true) {
                    if (test === false) {
                        showError(translate(value.message ?? 'app.common.mandatory_field', {}, 'validators'), $(this), value.custom)
                    } else {
                        showError(translate(test, {}, 'validators'), $(this), value.custom)
                    }
                    errors++;
                } else {
                    removeError($(this));
                }

            }
        })


    }

    return errors;
}

export function getFormValidation() {
    const main = {
        interest: {
            function: 'checkInterest',
            message: 'app.form.lead.select_interest',
            element: '#lead_interest',
            mandatory: true
        },
        // Champs numériques (nombre décimal / entier) : même contrôle que le serveur ; la fonction renvoie la clé du message
        numeric: {
            function: 'validateNumericField',
            element: 'input[data-numeric]'
        },
        seller: {
            function: 'checkIfSellerSelected',
            message: 'app.form.lead.select_seller',
            element: '#lead_team header',
            mandatory: true
        },
        source: {
            message: 'app.form.lead.select_source',
            element: '#lead_origin',
            mandatory: true
        },
        channel: {
            message: 'app.form.lead.select_type',
            element: '#lead_channel',
            mandatory: true
        },
        administrative: {
            function: 'validateAdministrativeSelect',
            message: 'app.form.lead.select_administrative',
            element: '.administrative-type .form-line select',
        },
        administrative_other: {
            function: 'validateAdministrativeOther',
            message: 'app.form.lead.select_administrative_type_other',
            element: '.administrative-type .form-line + .form-line input',
        },
        price: {
            function: 'validatePrice',
            message: 'app.request.invalid_price',
            element: '.form-line.--price input',
        },
        area: {
            function: 'validateArea',
            message: 'app.request.invalid_area',
            element: '.form-line.--area input',
        }
    };

    const pos = $('#lead_pos').val();
    const interest = getInterest();
    const specificFieldsRaw = window.MANDATORY_FIELDS?.[pos]?.[interest] || [];

    // Générer un objet pour les champs spécifiques
    const specificFieldsObj = Object.fromEntries(Object.entries(specificFieldsRaw));

    // Fusionner main et specificFieldsObj
    const finalFields = {
        ...main,
        ...specificFieldsObj
    };

    // Vérifier la présence de customer.email ou customer.phoneNumber
    if (!('customer.email' in finalFields) && !('customer.phoneNumber' in finalFields)) {
        // Ajouter le champ emailOrPhone si aucun des deux n'est présent
        finalFields['emailOrPhone'] = {
            function: 'emailOrPhone',
            message: 'app.form.lead.email_or_phone',
            element: '#lead_customer_email'
        };
    }

    return finalFields;
}

export function showMandatoryFields() {
    const check = getFormValidation();
    // Ne pas toucher aux champs de requests (mandatory fixé en base, data-class) ni aux popins
    // (RDV) hors .lead-form, dont les champs obligatoires sont fixes
    $('.lead-form .--mandatory').not('[data-class*="--mandatory"]').removeClass('--mandatory');

    for (const value of Object.values(check)) {
        if (value.mandatory) {
            let target = getTarget($(value.element));
            target.addClass('--mandatory');
        }
    }

    markMandatoryPlaceholders();
}

/**
 * Suffixe `*` au placeholder des champs texte obligatoires (les select2 l'ont en CSS, lead.scss).
 * Placeholder d'origine gardé en data-placeholder-base pour le restaurer quand le champ cesse
 * d'être obligatoire (changement de PDV / d'univers).
 */
export function markMandatoryPlaceholders() {
    $('[data-placeholder-base]').each(function () {
        this.placeholder = this.dataset.placeholderBase;
        delete this.dataset.placeholderBase;
    });

    $('.--mandatory, [data-class*="--mandatory"]').each(function () {
        const $fields = $(this).is('input, textarea') ? $(this) : $(this).find('input, textarea');
        $fields
            .not('[type="hidden"], .select2-search__field')
            .each(function () {
                if (!this.placeholder || this.dataset.placeholderBase !== undefined) return;
                this.dataset.placeholderBase = this.placeholder;
                this.placeholder += '*';
            });
    });
}

export function getInterest()
{
    return $('input[name="lead[interest]"]:checked').val();
}

/**
 * Affiche ou masque le champ "Autre" pour le type administratif
 * selon la valeur sélectionnée dans le select correspondant.
 * Gère également l'attribut "required" pour la validation native HTML5.
 *
 * @param {jQuery} element - L'élément select ou le conteneur administratif à traiter.
 */
export function checkAdministrativeValue(element) {
    let $block;

    if (element.hasClass('administrative-type')) {
        $block = element;
    } else {
        $block = element.closest('.administrative-type');
    }

    const $select = $block.find('select');
    const $otherLine = $block.children('.form-line').eq(1); // input block
    const $input = $otherLine.find('input');

    if ($select.val() === 'Autre') {
        $otherLine.removeClass('--hidden');
        $input.prop('required', true);
    } else {
        $otherLine.addClass('--hidden');
        $input.val('');
        $input.prop('required', false);
    }
}

/**
 * Version de compatibilité qui traite tous les éléments .administrative-type
 * Utilisée pour maintenir la compatibilité avec les anciens appels
 */
export function checkAdministrativeValueAll() {
    $('.administrative-type').each(function () {
        checkAdministrativeValue($(this));
    });
}

/**
 * URI de la page courante (chemin + query string) — équivalent JS de
 * `app.request.requesturi` côté Twig.
 *
 * @returns {string}
 */
export function currentUri()
{
    return window.location.pathname + window.location.search;
}

/**
 * Contexte de la page, posé par le template via `<div id="pageConfig" data-*>`.
 * Chaque page en déclare au plus un ; les écrans qui n'en ont pas renvoient un
 * objet vide, d'où la lecture au moment de l'usage plutôt qu'au chargement du
 * module (la plupart des scripts sont chargés sur toutes les pages).
 *
 * @returns {DOMStringMap|Object}
 */
export function pageConfig()
{
    return document.getElementById('pageConfig')?.dataset ?? {};
}

/** Lead clôturé, en attente de validation de perte ou hors univers du PDV : ni réaffectation ni changement de PDV. */
export function isLeadLocked()
{
    return pageConfig().leadLocked === '1';
}

/**
 * Numéro principal du client d'un pays non transférable (coût d'appel) : les boutons de transfert restent affichés
 * mais verrouillés, l'infobulle dit pourquoi. Le clic est bloqué dans cti-transfer.js, et le serveur refuse
 * aussi le transfert d'après le numéro transmis par la page.
 * Réversible : le titre d'origine (« Transférer au … ») est gardé pour le déverrouillage.
 */
export function applyTransferLock()
{
    const { transferLocked, transferLockedTitle } = pageConfig();
    $('.begin-transfer').each(function () {
        if (transferLocked === '1') {
            this.dataset.titleUnlocked ??= this.dataset.title;
            this.dataset.title = transferLockedTitle;
            this.classList.add('--locked');
        } else if (this.dataset.titleUnlocked !== undefined) {
            this.dataset.title = this.dataset.titleUnlocked;
            delete this.dataset.titleUnlocked;
            this.classList.remove('--locked');
        }
    });
}

/**
 * Numéro principal du client revalidé (phone-detector.js, verdict `transferAllowed` du serveur) : le verrou suit la
 * saisie sans attendre l'enregistrement, et le numéro évalué part avec la demande de transfert (contrôle serveur).
 */
export function updateTransferLock(customerPhone, allowed)
{
    const config = document.getElementById('pageConfig');
    // Page sans bouton de transfert (porteur sans data-transfer-locked) : rien à verrouiller.
    if (!config || config.dataset.transferLocked === undefined) {
        return;
    }
    config.dataset.transferLocked = allowed ? '0' : '1';
    config.dataset.transferPhone = customerPhone;
    applyTransferLock();
}

$(function(){
    toggleSectionContentVisibility();
    preventDoublon();
    applyTransferLock();
    launchTooltip();
})

